'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import { Check } from 'lucide-react';
import FeaturedVideoHero from '@/components/public/FeaturedVideoHero';

const steps = [
  {
    num: 1,
    label: 'Record',
    color: '#EF4444',
    title: 'Capture your conversations',
    desc: 'Your expertise, your way. Record with an expert guide, go solo with broadcast-quality audio, or upload what you already have.',
    features: [
      'Book guided conversations — we ask the questions that unlock insights',
      'Record solo with broadcast-quality audio & 1080p video',
      'Upload existing podcasts and recordings for instant analysis',
    ],
    quote: '"I used to hire a production team to come into the office once a year and record videos of my executive team. It cost a fortune and our marketing team hated trying to constantly re-cut old material. Now, they constantly get clips from all of our team — turning our leaders into thought leaders — automatically."',
    quoteAuthor: '— Founder & CEO, Design-led engineering company',
    image: '/product-shots/moments-timeline.png',
    imageAlt: 'Guided conversation with AI moment detection',
    reverse: false,
  },
  {
    num: 2,
    label: 'Analyze',
    color: '#3B82F6',
    title: 'AI finds what matters',
    desc: 'Momento AI detects 13 moment types automatically — quotes, insights, testimonials, breakthroughs. Search any conversation instantly using natural language.',
    features: [
      'Search like you talk: "customer testimonials about ROI"',
      'Domain-specific accuracy for biotech, deep tech, AI',
      'Find any moment in 200ms',
    ],
    quote: null,
    quoteAuthor: null,
    image: '/product-shots/momento-search.png',
    imageAlt: 'Momento AI search finding high-value moments',
    reverse: true,
  },
  {
    num: 3,
    label: 'Share',
    color: '#10B981',
    title: 'Export & share everywhere',
    desc: "One conversation becomes content for every platform. Share what resonates with you, when you're ready — in your authentic voice, not generic AI.",
    features: [
      'Export clips with burned-in captions for LinkedIn, X, YouTube',
      'Create standalone posts from edited moments',
      'Edit transcripts, preview changes, publish when perfect',
    ],
    quote: '"I used to constantly re-record demo content for prospects. With Apprendo, the first take always works and every demo I record adds to our library of material that we can pull from. Less looms and more time with leads — love it!"',
    quoteAuthor: '— Sales Leader, Technology company',
    image: '/product-shots/moment-cards.png',
    imageAlt: 'Export moments as LinkedIn posts, video clips, and more',
    reverse: false,
  },
];

export default function HowItWorksSection() {
  return (
    <section id="how-it-works" className="py-20 bg-card border-t border-b border-border">
      <div className="max-w-[1200px] mx-auto px-8">
        <div className="text-center mb-13">
          <div className="landing-section-label">How it works</div>
          <h2 className="text-[38px] font-bold tracking-[-1.5px] mb-3.5 text-foreground">
            Three steps. Zero blank pages.
          </h2>
          <p className="text-[17px] text-muted-foreground max-w-[540px] mx-auto">
            Your team has stories, insights, and expertise. They just don&apos;t have time to write it down.
          </p>
        </div>

        <div className="space-y-0">
          {steps.map((step, i) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8 }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-14 items-center py-14 ${
                i < steps.length - 1 ? 'border-b border-border' : ''
              }`}
            >
              {/* Text */}
              <motion.div
                initial={{ opacity: 0, x: step.reverse ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className={step.reverse ? 'lg:order-2' : ''}
              >
                <div className="inline-flex items-center gap-2 mb-4" style={{ fontFamily: 'var(--font-space-mono)' }}>
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-[13px]"
                    style={{ background: step.color }}
                  >
                    {step.num}
                  </div>
                  <span className="text-[11px] uppercase tracking-[1.5px]" style={{ color: step.color }}>
                    {step.label}
                  </span>
                </div>
                <h3 className="text-[30px] font-bold tracking-[-1px] mb-3 text-foreground">
                  {step.title}
                </h3>
                <p className="text-[16px] text-muted-foreground leading-[1.7] mb-6">
                  {step.desc}
                </p>
                <ul className="space-y-2.5">
                  {step.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[14px] text-foreground">
                      <Check className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                {step.quote && (
                  <div className="mt-6 p-4 px-5 bg-[#FAFAF9] rounded-[10px] border-l-[3px] border-[#EF4444]">
                    <p className="text-[13px] italic text-muted-foreground leading-[1.6]">{step.quote}</p>
                    <p className="text-[12px] font-semibold text-[#EF4444] mt-1.5">{step.quoteAuthor}</p>
                  </div>
                )}
              </motion.div>

              {/* Product mockup */}
              <motion.div
                initial={{ opacity: 0, x: step.reverse ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className={step.reverse ? 'lg:order-1' : ''}
              >
                {step.num === 1 ? (
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gray-900 h-[280px] sm:h-[360px] md:h-[420px]">
                    <FeaturedVideoHero />
                    <div className="absolute top-3 left-3 md:top-4 md:left-4">
                      <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-lg px-2.5 py-1 md:px-3 md:py-1.5 shadow-lg flex items-center gap-1.5">
                        <div className="w-2 h-2 bg-red-500 rounded-full" />
                        <span className="text-[10px] md:text-xs font-medium">Guided Conversation</span>
                      </div>
                    </div>
                    <div className="absolute top-3 right-3 md:top-4 md:right-4">
                      <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg px-2.5 py-1 md:px-3 md:py-1.5 shadow-lg flex items-center gap-1.5">
                        <span className="text-[10px] md:text-xs font-semibold">AI: Moments Detected</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-[#111827] rounded-[16px] overflow-hidden shadow-[0_12px_40px_rgba(0,0,0,0.12)]">
                    <div className="flex items-center gap-1.5 px-4 py-3 bg-[#1F2937]">
                      <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" />
                      <div className="w-2.5 h-2.5 rounded-full bg-[#EAB308]" />
                      <div className="w-2.5 h-2.5 rounded-full bg-[#22C55E]" />
                    </div>
                    <div className="p-5">
                      <Image
                        src={step.image}
                        alt={step.imageAlt}
                        width={600}
                        height={400}
                        className="w-full h-auto rounded-lg"
                      />
                    </div>
                  </div>
                )}
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
